import { RGBColor, color, hsl } from "d3-color";
import { Color } from "deck.gl";
import { deckGlColourToRgba } from "react-migration/domains/map/util/deckGlColourToRgba";

type ColourOptions = {
  darker?: number;
  brighter?: number;
  alpha?: number;
};

/**
 * Utility function to create a deck.gl compatible colour interpolator from a
 * colour interpolator. For a list of colour interpolators, [see d3-scale-chromatic](https://observablehq.com/@d3/color-schemes).
 *
 * @param colourInterpolator - A function that interpolates a normal number
 * (0-1) to an rgb string.
 * @returns A function that interpolates a norma number (0-1) to a deck.gl
 * compatible colour.
 *
 * @example
 * import { interpolateBrBG } from "d3-scale-chromatic";
 *
 * const interpolateDeckGlColour = createDeckGlInterpolator(interpolateBrBG);
 * const colour = interpolateDeckGlColour(0.5);
 */
export function createDeckGlColorInterpolator(
  colourInterpolator: (v: number) => string,
  { darker, brighter, alpha }: ColourOptions = {}
) {
  return (value: number) => {
    const rgbStr = colourInterpolator(value);
    let colour = hsl(color(rgbStr)!.formatHsl()!);

    if (darker) {
      colour = colour.darker(darker);
    } else if (brighter) {
      colour = colour.brighter(brighter);
    }

    return rgbToDeckGlColour(colour.rgb(), alpha);
  };
}

/**
 * Utility function to get an array of deck.gl compatible colours from a
 * colour interpolator. For a list of colour interpolators, [see d3-scale-chromatic](https://observablehq.com/@d3/color-schemes).
 *
 * @param colourInterpolator - A function that interpolates a normal number
 * (0-1) to an rgb string.
 * @param numberOfStops - The number of colour stops to return.
 * @returns An array of deck.gl compatible colours.
 *
 * @example
 * import { interpolateBrBG } from "d3-scale-chromatic";
 *
 * const colourStops = getDeckGlColourStops(interpolateBrBG, 10);
 */
export function getDeckGlColourStops(
  colourInterpolator: (v: number) => string,
  numberOfStops: number,
  colourOptions?: ColourOptions
) {
  const deckGlColourInterpolator = createDeckGlColorInterpolator(colourInterpolator, colourOptions);

  return Array.from({ length: numberOfStops }, (_, i) => {
    // Calculate the center of each segment
    // For 4 stops: segments are [0-0.25], [0.25-0.5], [0.5-0.75], [0.75-1.0]
    // Centers are: 0.125, 0.375, 0.625, 0.875
    const segmentSize = 1 / numberOfStops;
    const centerOfSegment = (i + 0.5) * segmentSize;
    return deckGlColourInterpolator(centerOfSegment);
  });
}

export function darkenDeckGlColour(colour: Color, amount: number) {
  let hslColour = hsl(color(deckGlColourToRgba(colour))!.formatHsl());

  hslColour = hslColour.darker(amount);

  return rgbToDeckGlColour(hslColour.rgb());
}

export function brightenDeckGlColour(colour: Color, amount: number) {
  let hslColour = hsl(color(deckGlColourToRgba(colour))!.formatHsl());

  hslColour = hslColour.brighter(amount);

  return rgbToDeckGlColour(hslColour.rgb());
}

export function rgbToDeckGlColour(rgb: RGBColor, alpha?: number): Color {
  return [rgb.r, rgb.g, rgb.b, alpha ?? 255] as Color;
}
